import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import { HiChevronRight, HiOutlineHome } from "react-icons/hi";
import { colors } from "../data/tokens";
import { groupForPath, iconForPath } from "../data/resourceIcons";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import AddIcon from "@mui/icons-material/Add";
import { useParams } from "react-router-dom";
import { FaEye, FaPencilAlt } from "react-icons/fa";
import { determineUserReadableRole } from "../ra-components/users/UserUtils";
import ImportButton from "../ra-components/users/CustomImport";

interface BreadcrumbProps {
  title: string;
  secondTitle?: string;
  firstPath?: string;
  secondPath?: string;
  thirdTitle?: string;
  actionType?: "create" | "show" | "edit" | "button";
  actionUrl?: string;
  actionText?: string;
  importButton?: boolean;
  defaultModel?: string;
  // Ações customizadas no lado direito (ex.: botão Enviar de formulário)
  actions?: React.ReactNode;
}

// Cada item do caminho: ícone e texto alinhados, sem sublinhado
const itemSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  fontSize: 14,
  lineHeight: 1.4,
  transition: "color 150ms",
};

export const PageTopBreadcrumb = (props: BreadcrumbProps) => {
  const userRole = localStorage.getItem("roles");

  const {
    title,
    firstPath,
    secondTitle,
    secondPath,
    thirdTitle,
    actionType,
    actionUrl,
    actionText,
  } = props;
  const { id } = useParams();
  // Ícone da seção, o mesmo usado no menu lateral
  const ResourceIcon = iconForPath(firstPath);
  // Seção do menu acima da tela (ex.: Gestão de Membros › Eleições).
  // O papel de sócio tem menu sem grupos — para ele, nada muda; e quando
  // o rótulo do grupo repete o título da tela (Transações), também não.
  const group =
    determineUserReadableRole(userRole) === "user"
      ? undefined
      : groupForPath(firstPath);
  return (
    <Grid
      container
      // direction="row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Grid item>
        {/* Separador é o chevron da família Heroicons (mesma da sidebar).
            Antes havia dois separadores concorrendo: a barra "/" padrão do
            MUI e um ArrowRightIcon dentro de cada item. */}
        <Breadcrumbs
          aria-label="breadcrumb"
          separator={<HiChevronRight size={14} color={colors.neutral300} />}
          sx={{
            paddingTop: 3,
            paddingBottom: 2,
            "& .MuiBreadcrumbs-separator": { marginX: 0.75 },
          }}
        >
          <Link
            underline="none"
            aria-label="Início"
            sx={{ ...itemSx, color: colors.textMuted, "&:hover": { color: colors.primary } }}
            href="/"
          >
            <HiOutlineHome size={16} />
          </Link>

          {group && group.label !== title && (
            // Grupo do menu: informativo, sem link (não é uma página)
            <Typography sx={{ ...itemSx, color: colors.textMuted }}>
              <group.Icon size={16} />
              {group.label}
            </Typography>
          )}

          {title && (
            firstPath ? (
              <Link
                underline="none"
                sx={{
                  ...itemSx,
                  color: colors.primary,
                  fontWeight: 600,
                  "&:hover": { color: colors.primaryDark },
                }}
                href={firstPath}
              >
                {/* Mesmo ícone que a seção tem no menu lateral */}
                <ResourceIcon size={16} />
                {title}
              </Link>
            ) : (
              <Typography sx={{ ...itemSx, color: colors.primary, fontWeight: 600 }}>
                <ResourceIcon size={16} />
                {title}
              </Typography>
            )
          )}

          {secondTitle && (
            secondPath ? (
              <Link
                underline="none"
                sx={{
                  ...itemSx,
                  color: colors.textMuted,
                  "&:hover": { color: colors.primary },
                }}
                href={secondPath}
              >
                {secondTitle}
              </Link>
            ) : (
              <Typography sx={{ ...itemSx, color: colors.textMuted }}>
                {secondTitle}
              </Typography>
            )
          )}

          {thirdTitle && (
            <Typography
              sx={{ ...itemSx, color: colors.textStrong, fontWeight: 600 }}
            >
              {thirdTitle}
            </Typography>
          )}
        </Breadcrumbs>
      </Grid>
      <Grid item>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            // Altura e padding lateral únicos para toda ação do topo
            // (mistura de tamanhos: ButtonGroup small × botões do RA)
            "& .MuiButton-root": {
              minHeight: 36,
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        >
        {props.actions}
        <ButtonGroup
          variant="contained"
          aria-label="outlined primary button group"
          size="small"
        >
          {props.importButton && (
            <ImportButton defaultModel={props.defaultModel} />
          )}
          {determineUserReadableRole(userRole) === "admin" &&
            actionType === "create" &&
            firstPath && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                href={firstPath + "/create"}
              >
                Novo
              </Button>
            )}
          {actionType === "show" && firstPath && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaEye />}
              href={firstPath + "/" + id + "/show"}
            >
              Ver
            </Button>
          )}
          {userRole &&
            (determineUserReadableRole(userRole) === "admin" ||
              determineUserReadableRole(userRole) === "user") &&
            actionType === "edit" &&
            firstPath && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<FaPencilAlt />}
                href={firstPath + "/" + id + "/edit"}
              >
                Alterar
              </Button>
            )}
          {actionType === "button" && (
            <Button variant="contained" color="primary" href={actionUrl}>
              {actionText}
            </Button>
          )}
        </ButtonGroup>
        </Box>
      </Grid>
    </Grid>
  );
};
